import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Form } from "react-bootstrap";
import Hint from "../shared/Hint.jsx";
import { dailyChartWeeks } from "./dailyChartWeeks.js";

// The search-page home visuals, split for compactness (real estate is premium):
//   <HomeStatsInline>  — the four headline numbers, small enough to live INSIDE
//                        the page-title row next to "Claim Search".
//   <HomeChart>        — the claims-logged-by-day bar strip (Streamline-style),
//                        with the admin-only "pretend today is …" anchor control.
// The page fetches /claims/home-stats once and feeds both. All real data from the
// status event log; bars wear the theme primary, text wears text tokens.

const dayLabel = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-NZ", { day: "2-digit", month: "short" });

function Stat({ label, value }) {
  return (
    <div className="text-nowrap">
      <div className="text-muted text-uppercase" style={{ fontSize: "0.62rem", letterSpacing: "0.06em" }}>
        {label}
      </div>
      <div className="fs-5 fw-semibold lh-1">{value}</div>
    </div>
  );
}

export function HomeStatsInline({ stats }) {
  if (!stats?.anchor) return null;
  return (
    <div className="d-flex align-items-center column-gap-3 flex-wrap">
      <Stat label={`Logged · wk to ${dayLabel(stats.anchor)}`} value={stats.week.logged} />
      <Stat label="Accepted" value={stats.week.accepted} />
      <Stat label="Completed" value={stats.week.completed} />
      <div className="vr" />
      <Stat label="Open jobs" value={stats.open_jobs} />
    </div>
  );
}

// Hand-rolled SVG column chart: ≤24px bars, 4px rounded data-end (square at the
// baseline), 2px surface gaps, hairline gridlines, Monday labels with weekly
// totals and full-height shared hover hints. Positive bars carry a permanent
// value label so the daily count is readable without a mouse.
function DailyBars({ daily }) {
  // Measure the container so the chart fills ANY width edge-to-edge in real
  // pixels — a fixed viewBox letterboxes on wide (1080p+) screens.
  const wrapRef = useRef(null);
  const [W, setW] = useState(980);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(480, el.clientWidth)));
    ro.observe(el);
    setW(Math.max(480, el.clientWidth));
    return () => ro.disconnect();
  }, []);
  const H = 118;
  const PAD = { top: 16, right: 4, bottom: 18, left: 22 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const max = Math.max(1, ...daily.map((d) => d.logged));
  const weeks = new Map(dailyChartWeeks(daily).map((week) => [week.weekStart, week]));
  const slot = plotW / daily.length;
  const barW = Math.min(24, Math.max(4, slot - 2)); // 2px surface gap between bars
  const y = (v) => PAD.top + plotH - (v / max) * plotH;

  const step = max <= 4 ? 1 : Math.ceil(max / 4);
  const ticks = [];
  for (let t = 0; t <= max; t += step) ticks.push(t);

  return (
    <div ref={wrapRef}>
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label="Claims logged by day" className="home-band-chart">
      {ticks.map((t) => (
        <g key={t}>
          <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} className="grid" />
          <text x={PAD.left - 5} y={y(t) + 3} textAnchor="end" className="tick">{t}</text>
        </g>
      ))}
      {daily.map((d, i) => {
        const cx = PAD.left + i * slot + slot / 2;
        const week = weeks.get(d.day); // week-start dates are Mondays
        const h = Math.max(0, (d.logged / max) * plotH);
        const top = y(d.logged);
        const r = d.logged > 0 ? Math.min(4, h / 2, barW / 2) : 0;
        const path =
          d.logged > 0
            ? `M ${cx - barW / 2} ${PAD.top + plotH}
               V ${top + r}
               Q ${cx - barW / 2} ${top} ${cx - barW / 2 + r} ${top}
               H ${cx + barW / 2 - r}
               Q ${cx + barW / 2} ${top} ${cx + barW / 2} ${top + r}
               V ${PAD.top + plotH} Z`
            : null;
        const hint = `${dayLabel(d.day)} — ${d.logged} claim${d.logged === 1 ? "" : "s"} logged`;
        return (
          <g key={d.day} className="bar-slot">
            {path ? <path d={path} className="bar" /> : null}
            {d.logged > 0 ? (
              <text x={cx} y={Math.max(10, top - 4)} textAnchor="middle" className="bar-value">
                {d.logged}
              </text>
            ) : null}
            {week ? (
              <text x={cx} y={H - 5} textAnchor={cx < 70 ? "start" : cx > W - 70 ? "end" : "middle"}
                className="tick" aria-label={`Week of ${dayLabel(d.day)}: ${week.total} claims logged`}
                data-hint={`${dayLabel(week.firstDay)}–${dayLabel(week.lastDay)} · ${week.total} claims logged in the displayed days`}>
                {dayLabel(d.day)}<tspan dx="12" className="week-total">Total {week.total}</tspan>
              </text>
            ) : null}
            <rect x={cx - slot / 2} y={PAD.top} width={slot} height={plotH} className="hit"
              data-hint={hint} aria-label={hint} />
          </g>
        );
      })}
    </svg>
    </div>
  );
}

// A stored anchor that is a fixed calendar date ("pretend today is …"), as
// opposed to the two FOLLOWING modes, 'latest' and 'today'.
const pinnedDate = (mode) => /^\d{4}-\d{2}-\d{2}$/.test(String(mode || ""));

export function HomeChart({ stats, isAdmin = false, onAnchorChange }) {
  if (!stats?.anchor || !stats.daily?.some((d) => d.logged > 0)) return null;
  const weekEnding = dayLabel(stats.anchor);
  return (
    <div className="border rounded px-3 pt-2 pb-1 mb-3 home-band">
      <span className="text-muted text-uppercase" style={{ fontSize: "0.68rem", letterSpacing: "0.06em" }}>
        Claims logged by day
      </span>
      <span className="text-muted ms-2" style={{ fontSize: "0.68rem" }}>
        · 5 weeks to {weekEnding}
      </span>
      {isAdmin ? (
        // THREE MODES, not a bare date box (Robert 2026-08-21). The control
        // used to offer only "pin this date" or blank-for-latest, with no way
        // to say FOLLOW THE CLOCK — so both dev and the demo ended up pinned
        // to a date that was right the day it was typed and stale the next
        // morning (dev sat on 7 July for six weeks). "Today" is now a mode,
        // and it is the one a live system wants.
        <span className="float-end d-inline-flex align-items-center gap-1">
          <Hint label="Which day the band ends on: the latest activity in the data, today's date, or a fixed date to demo from">
            <ThemedSelect
              size="sm"
              style={{ width: 130, fontSize: "0.72rem", padding: "0 6px" }}
              value={pinnedDate(stats.anchor_mode) ? "date" : stats.anchor_mode === "today" ? "today" : "latest"}
              onChange={(e) =>
                onAnchorChange?.(e.target.value === "date" ? stats.anchor : e.target.value)
              }
            >
              <option value="latest">Latest activity</option>
              <option value="today">Today</option>
              <option value="date">Fixed date…</option>
            </ThemedSelect>
          </Hint>
          {pinnedDate(stats.anchor_mode) ? (
            <Form.Control
              type="date"
              size="sm"
              style={{ width: 140, fontSize: "0.72rem", padding: "0 6px" }}
              value={stats.anchor_mode}
              onChange={(e) => onAnchorChange?.(e.target.value)}
            />
          ) : null}
        </span>
      ) : null}
      <DailyBars daily={stats.daily} />
    </div>
  );
}
